import ROLE from "models/role";
import { SHARED_ROUTES } from "definitions/constants/routeConstants";
import ProfilePage from "pages/ProfilePage";
import SettingsPage from "pages/SettingsPage";
import Home from "pages/Home";
import EmployeePage from "pages/EmployeePage";
import HangoutPage from "pages/HangoutPage";
import NotFoundPage from "pages/NotFoundPage";
import EmailsPage from "pages/EmailsPage";
import EmailPage from "pages/PeopleTeam/Resources/Email/EmailPage";
import MeetingEventsPage from "pages/PeopleTeam/MeetingEventsPage";
import MeetingEventPage from "pages/PeopleTeam/MeetingEventPage";
import MeetingPage from "pages/PeopleTeam/Resources/Meeting/MeetingPage";
import MessagesPage from "pages/MessagesPage";
import MessagePage from "pages/PeopleTeam/Resources/Message/MessagePage";
import MyJourneysPage from "pages/MyJourneysPage";
import MyTasksPage from "pages/Shared/Tasks/MyTasksPage";
import TasksPage from "pages/TasksPage";
import TaskPage from "pages/PeopleTeam/Resources/Task/TaskPage";
import FormResponsePage from "pages/FormResponsePage";
import IndividualPage from "pages/IndividualSettingsPage";
import NewHirePortalPage from "pages/NewHirePortalPage";
import NewHireTasksPage from "pages/NewHireTasksPage";
import NewHireSchedulePage from "pages/NewHireSchedulePage";
import CheckpointPage from "pages/PeopleTeam/Resources/Checkpoint/CheckpointPage";
import { Route } from "./types";

const SHARED_ROUTES_PERMISSIONS = {
  permission: Object.values(ROLE),
};

// Routes that pre-start (personal-email) sessions are explicitly permitted to reach.
// All other authenticated routes deny personal-email sessions by default; backend
// is authoritative — this is defense-in-depth so the URL can't be typed directly.
const PERSONAL_EMAIL_ALLOWED_ROUTES_PERMISSIONS = {
  permission: Object.values(ROLE),
  allowPersonalEmail: true,
};

const SHARED_HOME_ROUTE: Route = {
  path: SHARED_ROUTES.home,
  title: "Home",
  component: <Home />,
  ...PERSONAL_EMAIL_ALLOWED_ROUTES_PERMISSIONS,
};

const SHARED_NOT_FOUND_ROUTE: Route = {
  path: SHARED_ROUTES.notFound,
  title: "Not Found",
  component: <NotFoundPage />,
  ...PERSONAL_EMAIL_ALLOWED_ROUTES_PERMISSIONS,
};

const SHARED_PROFILE_ROUTE: Route = {
  path: SHARED_ROUTES.profile,
  title: "Profile",
  component: <ProfilePage />,
  ...PERSONAL_EMAIL_ALLOWED_ROUTES_PERMISSIONS,
};

const SHARED_INDIVIDUAL_SETTINGS_ROUTE: Route = {
  path: SHARED_ROUTES.individualSettings,
  title: "Individual Settings",
  component: <IndividualPage />,
  ...PERSONAL_EMAIL_ALLOWED_ROUTES_PERMISSIONS,
};

const SHARED_SETTINGS_ROUTE: Route = {
  path: SHARED_ROUTES.settings,
  title: "Settings",
  component: <SettingsPage />,
  ...SHARED_ROUTES_PERMISSIONS,
};

const SHARED_SHOW_USER_ROUTE: Route = {
  path: SHARED_ROUTES.showUser,
  title: "View User",
  component: <EmployeePage />,
  ...SHARED_ROUTES_PERMISSIONS,
};

const SHARED_LIST_EMAILS_ROUTE: Route = {
  path: SHARED_ROUTES.emails.index,
  title: "List Emails",
  component: <EmailsPage />,
  ...SHARED_ROUTES_PERMISSIONS,
};

const SHARED_SHOW_EMAIL_ROUTE: Route = {
  path: SHARED_ROUTES.emails.show,
  title: "Show Email",
  component: <EmailPage />,
  ...SHARED_ROUTES_PERMISSIONS,
};

const SHARED_LIST_MEETING_EVENTS_ROUTE: Route = {
  path: SHARED_ROUTES.meetingEvents.index,
  title: "List Meetings",
  component: <MeetingEventsPage />,
  ...SHARED_ROUTES_PERMISSIONS,
};

const SHARED_SHOW_MEETING_EVENT_ROUTE: Route = {
  path: SHARED_ROUTES.meetingEvents.show,
  title: "Show Meeting Event",
  component: <MeetingEventPage />,
  ...SHARED_ROUTES_PERMISSIONS,
};

const SHARED_SHOW_MEETING_ROUTE: Route = {
  path: SHARED_ROUTES.meetings.show,
  title: "Show Meeting",
  component: <MeetingPage />,
  ...SHARED_ROUTES_PERMISSIONS,
};

const SHARED_LIST_MESSAGES_ROUTE: Route = {
  path: SHARED_ROUTES.messages.index,
  title: "List Messages",
  component: <MessagesPage />,
  ...SHARED_ROUTES_PERMISSIONS,
};

const SHARED_SHOW_MESSAGE_ROUTE: Route = {
  path: SHARED_ROUTES.messages.show,
  title: "Show Message",
  component: <MessagePage />,
  ...SHARED_ROUTES_PERMISSIONS,
};

const SHARED_SHOW_TASK_ROUTE: Route = {
  path: SHARED_ROUTES.tasks.show,
  title: "Show Task",
  component: <TaskPage />,
  ...SHARED_ROUTES_PERMISSIONS,
};

const SHARED_FORM_RESPONSE_ROUTE: Route = {
  path: SHARED_ROUTES.formResponse,
  title: "Form",
  component: <FormResponsePage />,
  ...SHARED_ROUTES_PERMISSIONS,
};

const SHARED_LIST_TASKS_V2_ROUTE: Route = {
  path: SHARED_ROUTES.tasks.index,
  title: "List Tasks",
  component: <TasksPage />,
  ...SHARED_ROUTES_PERMISSIONS,
};

const SHARED_MY_TASKS_ROUTE: Route = {
  path: SHARED_ROUTES.myTasks,
  title: "My Tasks",
  component: <MyTasksPage />,
  ...PERSONAL_EMAIL_ALLOWED_ROUTES_PERMISSIONS,
};

const SHARED_MY_JOURNEYS_ROUTE: Route = {
  path: SHARED_ROUTES.myJourneys,
  title: "My Journeys",
  component: <MyJourneysPage />,
  ...PERSONAL_EMAIL_ALLOWED_ROUTES_PERMISSIONS,
};

const SHARED_NEW_HIRE_PORTAL_ROUTE: Route = {
  path: SHARED_ROUTES.newHirePortal,
  title: "My Onboarding",
  component: <NewHirePortalPage />,
  ...PERSONAL_EMAIL_ALLOWED_ROUTES_PERMISSIONS,
};

const SHARED_NEW_HIRE_TASKS_ROUTE: Route = {
  path: SHARED_ROUTES.newHireTasks,
  title: "My Tasks",
  component: <NewHireTasksPage />,
  ...PERSONAL_EMAIL_ALLOWED_ROUTES_PERMISSIONS,
};

const SHARED_NEW_HIRE_SCHEDULE_ROUTE: Route = {
  path: SHARED_ROUTES.newHireSchedule,
  title: "My Schedule",
  component: <NewHireSchedulePage />,
  ...PERSONAL_EMAIL_ALLOWED_ROUTES_PERMISSIONS,
};

const SHARED_SHOW_CHECKPOINT_ROUTE: Route = {
  path: SHARED_ROUTES.checkpoints.show,
  title: "Show Checkpoint",
  component: <CheckpointPage />,
  ...SHARED_ROUTES_PERMISSIONS,
};

const SHARED_HANGOUT_ROUTE: Route = {
  path: SHARED_ROUTES.hangout,
  title: "Hangout",
  component: <HangoutPage />,
  ...SHARED_ROUTES_PERMISSIONS,
};

export default [
  SHARED_HOME_ROUTE,
  SHARED_NOT_FOUND_ROUTE,
  SHARED_PROFILE_ROUTE,
  SHARED_INDIVIDUAL_SETTINGS_ROUTE,
  SHARED_SETTINGS_ROUTE,
  SHARED_SHOW_USER_ROUTE,
  SHARED_LIST_EMAILS_ROUTE,
  SHARED_SHOW_EMAIL_ROUTE,
  SHARED_LIST_MEETING_EVENTS_ROUTE,
  SHARED_SHOW_MEETING_EVENT_ROUTE,
  SHARED_SHOW_MEETING_ROUTE,
  SHARED_LIST_MESSAGES_ROUTE,
  SHARED_SHOW_MESSAGE_ROUTE,
  SHARED_LIST_TASKS_V2_ROUTE,
  SHARED_FORM_RESPONSE_ROUTE,
  SHARED_MY_TASKS_ROUTE,
  SHARED_MY_JOURNEYS_ROUTE,
  SHARED_NEW_HIRE_PORTAL_ROUTE,
  SHARED_NEW_HIRE_TASKS_ROUTE,
  SHARED_NEW_HIRE_SCHEDULE_ROUTE,
  SHARED_SHOW_CHECKPOINT_ROUTE,
  SHARED_SHOW_TASK_ROUTE,
  SHARED_HANGOUT_ROUTE,
];
